import React, {useContext, useMemo, useState} from 'react';
import {Switch, Checkbox, Button, Modal} from 'antd';
import dayjs from "dayjs";
import {Sorter} from "../../../shared/components/Sorter";
import IsLoading from "../../../shared/components/IsLoading";
import ErrorPage from "../../../shared/components/ErrorPage";
import TablePage from "../../../pages/tablePage/TablePage";
import {useShoppingRoad, useShoppingRoadManager, useFetchShoppingRoadGroup, useChangeShoppingRoadOnOff} from "./useShoppingRoad";
import ShoppingRoadEditModal from "./ShoppingRoadEditModal";
import toast from "react-hot-toast";
import {ENV, REFRESH_TYPE_FUNCTION} from "../../../shared/utils/enum";
import {useReloadTime} from "../../serverInfo/useServerInfo";
import {AuthContext} from "../../../shared/contexts/authContext";
import {internalApi} from "../../../common/axios";

const ShoppingRoad = () => {
    const {currentUser} = useContext(AuthContext);
    const [showExpired, setShowExpired] = useState(false);
    const {data, isLoading, error, refetch} = useShoppingRoad({showExpired});
    const {data: managerData, refetch: refetchManager} = useShoppingRoadManager();
    const {data: groupData, refetch: refetchGroup} = useFetchShoppingRoadGroup();
    const {data: reloadTimeData, refetch: reloadTimeDataRefetch} = useReloadTime();
    const {mutate: changeOnOff} = useChangeShoppingRoadOnOff();

    const handleCopyLiveData = async () => {
        if (currentUser.target === ENV.PRODUCTION) {
            toast.error('운영 환경에서는 라이브 데이터 복사 기능을 사용할 수 없습니다.');
            return;
        }
        const result = await internalApi.put('/events/shopping_road');
        if (result) {
            toast.success('라이브 데이터 복사가 완료되었습니다.');
            void refetch();
            void refetchManager();
            void refetchGroup();
        }
    };

    const showCopyConfirm = () => {
        Modal.confirm({
            title: 'Live 데이터 복사',
            content: '정말로 Live 데이터를 복사하시겠습니까? (shopping_road_master, shopping_road_event_group, shopping_road_manager 전체 교체)',
            okText: '확인',
            cancelText: '취소',
            onOk: handleCopyLiveData,
        });
    };
    const [rowData, setRowData] = useState(null);

    const managerMap = useMemo(() => {
        const map = {};
        (managerData?.rows || []).forEach(m => { map[m.seq] = m; });
        return map;
    }, [managerData]);

    const groupMap = useMemo(() => {
        const map = {};
        (groupData?.rows || []).forEach(row => {
            if (!map[row.groupIdx]) map[row.groupIdx] = row.memo || '';
        });
        return map;
    }, [groupData]);

    const columns = useMemo(() => ([
        {
            title: '시퀀스',
            dataIndex: 'seq',
            key: 'seq',
            search: true,
        },
        {
            title: 'Memo',
            dataIndex: 'memo',
            key: 'memo',
        },
        {
            title: '계정 레벨',
            dataIndex: 'openLevel',
            key: 'openLevel',
        },
        {
            title: 'On/Off',
            dataIndex: 'onOff',
            key: 'onOff',
            filters: [{text: 'On', value: 1}, {text: 'Off', value: 0}],
            onFilter: (value, record) => record.onOff === value,
            render: (val, record) => (
                <div onClick={e => e.stopPropagation()}>
                    <Switch
                        checked={val === 1}
                        onChange={checked => {
                            changeOnOff(
                                {seq: record.seq, onOff: checked ? 1 : 0},
                                {onError: () => toast.error('On/Off 변경 실패')}
                            );
                        }}
                    />
                </div>
            ),
        },
        {
            title: 'Start',
            dataIndex: 'begin',
            render: (begin) => dayjs.unix(begin).format('YYYY-MM-DD HH:mm:ss'),
            key: 'begin',
        },
        {
            title: 'End',
            dataIndex: 'end',
            render: (end) => dayjs.unix(end).format('YYYY-MM-DD HH:mm:ss'),
            sorter: (a, b) => Sorter.DATE(a.end, b.end),
            key: 'end',
        },
        {
            title: '매니저',
            dataIndex: 'managerIdx',
            key: 'managerIdx',
            search: true,
            onFilter: (value, record) => {
                const m = managerMap[record.managerIdx];
                const text = m ? `${record.managerIdx}${m.memo ? `(${m.memo})` : ''}` : String(record.managerIdx ?? '');
                return text.toLowerCase().includes(value.toLowerCase());
            },
            render: v => {
                const m = managerMap[v];
                return m ? `${v}${m.memo ? `(${m.memo})` : ''}` : v || '-';
            },
        },
        {
            title: '그룹',
            dataIndex: 'groupIdx',
            key: 'groupIdx',
            search: true,
            onFilter: (value, record) => {
                const memo = groupMap[record.groupIdx];
                const text = `${record.groupIdx}${memo ? `(${memo})` : ''}`;
                return text.toLowerCase().includes(value.toLowerCase());
            },
            render: v => {
                const memo = groupMap[v];
                return `${v}${memo ? `(${memo})` : ''}`;
            },
        },
        {
            title: '패스',
            dataIndex: 'passIdx',
            key: 'passIdx',
        }
    ]), [changeOnOff, managerMap, groupMap]);

    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;

    const props = {
        columns: columns,
        localData: data || [],
        modal: ShoppingRoadEditModal,
        buttonText: '신규 쇼핑 로드 추가',
        rowData: rowData,
        setRowData: setRowData,
        modalResizable: true,
        modalOptions: {
            managerRows: managerData?.rows || [],
            groupRows: groupData?.rows || [],
        },
        refreshTypeFunc: REFRESH_TYPE_FUNCTION.GAME_DATA,
        reloadTime: reloadTimeData?.game_data_sync_id,
        reloadTimeRefetch: reloadTimeDataRefetch,
    };

    return (
        <div>
            {currentUser.target !== ENV.PRODUCTION && (
                <Button
                    style={{marginBottom: 16, marginRight: 16}}
                    onClick={showCopyConfirm}
                >
                    Live 데이터 복사하기
                </Button>
            )}
            <div style={{padding: '0 10px 8px'}}>
                <Checkbox
                    checked={showExpired}
                    onChange={e => setShowExpired(e.target.checked)}
                >
                    만료된 항목 포함 조회
                </Checkbox>
            </div>
            <TablePage {...props}/>
        </div>
    );
};

export default ShoppingRoad;
